import React, { useState, useEffect } from 'react';
import { useMatterNumbering } from '../../../../hooks/useMatterNumbering';
import type { 
  MatterNumberingTemplate, 
  MatterNumberingConfig 
} from '../../../../../../types/matterNumbering';

interface PatternBuilderProps {
  pattern: string;
  onPatternChange: (pattern: string) => void;
  config?: MatterNumberingConfig | null;
  onConfigChange?: (config: MatterNumberingConfig) => void;
}

interface PatternElement {
  id: string;
  name: string;
  description: string;
  pattern: string;
  category: 'date' | 'client' | 'service' | 'sequence' | 'template';
  isTemplate?: boolean;
  practiceArea?: string;
}

interface ValidationStatus {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

interface PatternPreview {
  examples: string[];
  isLoading: boolean;
  error?: string;
}

// Static built-in elements - no computation needed
const BUILT_IN_ELEMENTS: PatternElement[] = [
  {
    id: 'YYYY',
    name: '4-Digit Year',
    description: 'Full year (e.g., 2025)',
    pattern: '{{YYYY}}',
    category: 'date'
  },
  {
    id: 'YY',
    name: '2-Digit Year',
    description: 'Short year (e.g., 25)',
    pattern: '{{YY}}',
    category: 'date'
  },
  {
    id: 'MM',
    name: 'Month Number',
    description: 'Month as number (01-12)',
    pattern: '{{MM}}',
    category: 'date'
  },
  {
    id: 'MMM',
    name: 'Month Short',
    description: 'Short month name (Jan, Feb)',
    pattern: '{{MMM}}',
    category: 'date'
  },
  {
    id: 'clientCode',
    name: 'Client Code',
    description: 'Client identifier code',
    pattern: '{{clientCode}}',
    category: 'client'
  },
  {
    id: 'clientNumber',
    name: 'Client Number',
    description: 'Client sequential number',
    pattern: '{{clientNumber}}',
    category: 'client'
  },
  {
    id: 'clientInitials',
    name: 'Client Initials',
    description: 'Client name initials',
    pattern: '{{clientInitials}}',
    category: 'client'
  },
  {
    id: 'practiceArea',
    name: 'Service Area',
    description: 'Service area code',
    pattern: '{{practiceArea}}',
    category: 'service'
  },
  {
    id: 'practiceCode',
    name: 'Service Code',
    description: 'Service area short code',
    pattern: '{{practiceCode}}',
    category: 'service'
  },
  {
    id: 'sequence',
    name: 'Sequential Number',
    description: 'Auto-incrementing number',
    pattern: '{{sequence}}',
    category: 'sequence'
  },
  {
    id: 'sequence3',
    name: '3-Digit Sequence',
    description: 'Padded to 3 digits (001, 002)',
    pattern: '{{sequence:3}}',
    category: 'sequence'
  },
  {
    id: 'sequence4',
    name: '4-Digit Sequence',
    description: 'Padded to 4 digits (0001, 0002)',
    pattern: '{{sequence:4}}',
    category: 'sequence'
  },
];

export const PatternBuilder: React.FC<PatternBuilderProps> = ({
  pattern,
  onPatternChange,
  config,
  onConfigChange
}) => {
  const [selectedElements, setSelectedElements] = useState<PatternElement[]>([]);
  const [separator, setSeparator] = useState('-');
  const [validation, setValidation] = useState<ValidationStatus>({
    isValid: true,
    errors: [],
    warnings: []
  });
  const [patternPreview, setPatternPreview] = useState<PatternPreview>({
    examples: [],
    isLoading: false,
    error: undefined
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const { validateConfig, saveConfig } = useMatterNumbering();

  // Extract selected elements from pattern on load
  useEffect(() => {
    if (pattern) {
      const elements: PatternElement[] = [];
      const patternParts = pattern.split(separator);
      
      patternParts.forEach(part => {
        const cleanPart = part.trim();
        if (cleanPart.startsWith('{{') && cleanPart.endsWith('}}')) {
          const builtInElement = BUILT_IN_ELEMENTS.find(el => el.pattern === cleanPart);
          if (builtInElement) {
            elements.push(builtInElement);
          }
        }
      });
      
      setSelectedElements(elements);
    }
  }, [pattern, separator]);

  // Validation effect
  useEffect(() => {
    if (pattern && validateConfig) {
      const validatePattern = async () => {
        try {
          const result = await validateConfig({ 
            defaultTemplate: { 
              id: 'temp',
              pattern, 
              name: 'temp', 
              description: '', 
              category: 'specialized', 
              complexity: 2,
              status: 'enabled',
              examples: [], 
              practiceAreas: 'all' 
            },
            customTemplates: config?.customTemplates || [],
            practiceAreaConfigs: config?.practiceAreaConfigs || [],
            globalSettings: config?.globalSettings || {
              sequencePadding: 3,
              sequenceStart: 1,
              resetSequence: 'yearly',
              separator: '-',
              caseSensitive: false,
              allowDuplicates: false,
              validationRules: []
            },
            isEnabled: true,
            realm_id: '',
            lastUpdatedBy: 'user',
            lastUpdatedDate: new Date().toISOString()
          });
          
          setValidation({
            isValid: result.isValid,
            errors: Array.isArray(result.errors) 
              ? result.errors.map(error => typeof error === 'string' ? error : error.message)
              : [],
            warnings: Array.isArray(result.warnings)
              ? result.warnings.map(warning => typeof warning === 'string' ? warning : warning.message)
              : []
          });
        } catch (error) {
          setValidation({
            isValid: false,
            errors: ['Validation failed'],
            warnings: []
          });
        }
      };

      validatePattern();
    }
  }, [pattern, validateConfig, config]);

  // Preview generation effect - simple local implementation
  useEffect(() => {
    if (pattern && validation.isValid) {
      const generatePatternPreview = async () => {
        setPatternPreview(prev => ({ ...prev, isLoading: true, error: undefined }));
        
        try {
          // Simple local preview generation
          const examples = [];
          const now = new Date();
          
          for (let i = 0; i < 3; i++) {
            let example = pattern;
            const date = new Date(now.getTime() + (i * 24 * 60 * 60 * 1000));
            
            example = example
              .replace(/\{\{YYYY\}\}/g, String(date.getFullYear()))
              .replace(/\{\{YY\}\}/g, String(date.getFullYear()).slice(-2))
              .replace(/\{\{MM\}\}/g, String(date.getMonth() + 1).padStart(2, '0'))
              .replace(/\{\{MMM\}\}/g, ['Jan', 'Feb', 'Mar'][date.getMonth() % 3])
              .replace(/\{\{clientCode\}\}/g, ['SMITH', 'JOHNSON', 'WILLIAMS'][i])
              .replace(/\{\{clientNumber\}\}/g, String(i + 1001))
              .replace(/\{\{clientInitials\}\}/g, ['JS', 'MJ', 'RW'][i])
              .replace(/\{\{practiceArea\}\}/g, ['OPS', 'CS', 'PD'][i])
              .replace(/\{\{practiceCode\}\}/g, ['OP', 'CS', 'PD'][i])
              .replace(/\{\{sequence:\d+\}\}/g, String(i + 1).padStart(3, '0'))
              .replace(/\{\{sequence\}\}/g, String(i + 1));
            
            examples.push(example);
          }
          
          setPatternPreview({
            examples,
            isLoading: false,
            error: undefined
          });
        } catch (error) {
          setPatternPreview({
            examples: [],
            isLoading: false,
            error: 'Failed to generate preview'
          });
        }
      };

      const debounceTimer = setTimeout(generatePatternPreview, 300);
      return () => clearTimeout(debounceTimer);
    } else {
      setPatternPreview({ examples: [], isLoading: false, error: undefined });
    }
  }, [pattern, validation.isValid]);

  // Add element to pattern
  const addElement = (element: PatternElement) => {
    if (element.isTemplate) {
      // For templates, replace entire pattern
      const newPattern = element.pattern;
      onPatternChange(newPattern);
    } else {
      // For regular elements, add to existing pattern
      const newElements = [...selectedElements, element];
      const newPattern = newElements.map(el => el.pattern).join(separator);
      setSelectedElements(newElements);
      onPatternChange(newPattern);
    }
  };

  // Remove element from pattern
  const removeElement = (index: number) => {
    const newElements = selectedElements.filter((_, i) => i !== index);
    const newPattern = newElements.map(el => el.pattern).join(separator);
    setSelectedElements(newElements);
    onPatternChange(newPattern);
  };

  // Clear all elements
  const clearAllElements = () => {
    setSelectedElements([]);
    onPatternChange('');
  };

  // Handle direct pattern input change
  const handlePatternInputChange = (newPattern: string) => {
    onPatternChange(newPattern);
  };

  // Handle separator change
  const handleSeparatorChange = (newSeparator: string) => {
    setSeparator(newSeparator);
    if (selectedElements.length > 0) {
      const newPattern = selectedElements.map(el => el.pattern).join(newSeparator);
      onPatternChange(newPattern);
    }
  };

  // Save template as new template in config
  const saveTemplate = async () => {
    if (!templateName.trim() || !pattern.trim() || !config) return;

    setSaveStatus('saving');

    try {
      const newTemplate: MatterNumberingTemplate = {
        id: `custom-${Date.now()}`,
        name: templateName.trim(),
        description: templateDescription.trim() || `Custom template: ${templateName}`,
        pattern: pattern,
        category: 'specialized',
        examples: patternPreview.examples.slice(0, 2),
        practiceAreas: 'all',
        isCustom: true,
        status: 'enabled',
        complexity: Math.min(5, Math.max(1, selectedElements.length)) as 1 | 2 | 3 | 4 | 5,
        createdAt: new Date().toISOString(),
        lastUpdatedDate: new Date().toISOString(),
        createdBy: 'user',
        lastUpdatedBy: 'user'
      };

      const updatedConfig = {
        ...config,
        customTemplates: [...(config.customTemplates || []), newTemplate]
      };

      const savedConfig = await saveConfig(updatedConfig);
      
      if (onConfigChange) {
        onConfigChange(savedConfig);
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);

      // Close modal and reset form
      setShowSaveModal(false);
      setTemplateName('');
      setTemplateDescription('');

    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    }
  };

  // Open save template modal
  const handleSaveTemplate = () => {
    if (pattern.trim() && validation.isValid) {
      setShowSaveModal(true);
    }
  };

  // Group elements by category - simple computation, no custom templates in builder
  const groupedElements = {
    'Date Elements': BUILT_IN_ELEMENTS.filter(el => el.category === 'date'),
    'Client Elements': BUILT_IN_ELEMENTS.filter(el => el.category === 'client'),
    'Service Area Elements': BUILT_IN_ELEMENTS.filter(el => el.category === 'service'),
    'Sequence Elements': BUILT_IN_ELEMENTS.filter(el => el.category === 'sequence'),
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 pb-3">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Pattern Builder
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Build custom engagement numbering patterns with real-time validation and preview.
        </p>
      </div>

      {/* Validation Status */}
      {(!validation.isValid || validation.warnings.length > 0) && (
        <div className="space-y-2">
          {validation.errors.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md dark:bg-red-900/20 dark:border-red-600">
              <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
                Validation Errors
              </h4>
              <ul className="text-sm text-red-600 dark:text-red-400 space-y-1">
                {validation.errors.map((error, index) => (
                  <li key={index} className="flex items-start space-x-1">
                    <span>•</span>
                    <span>{error}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {validation.warnings.length > 0 && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md dark:bg-yellow-900/20 dark:border-yellow-600">
              <h4 className="text-sm font-medium text-yellow-700 dark:text-yellow-300 mb-1">
                Warnings
              </h4>
              <ul className="text-sm text-yellow-600 dark:text-yellow-400 space-y-1">
                {validation.warnings.map((warning, index) => (
                  <li key={index} className="flex items-start space-x-1">
                    <span>•</span>
                    <span>{warning}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Current Pattern */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4 dark:bg-gray-800 dark:border-gray-700">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Current Pattern
        </label>
        <div className="flex space-x-3 mb-3">
          <input
            type="text"
            value={pattern}
            onChange={(e) => handlePatternInputChange(e.target.value)}
            className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:text-white ${
              validation.isValid 
                ? 'border-gray-300 dark:border-gray-600' 
                : 'border-red-300 dark:border-red-600'
            }`}
            placeholder="e.g., {{YYYY}}-{{clientCode}}-{{sequence:3}}"
          />
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600 dark:text-gray-400">Separator:</label>
            <input
              type="text"
              value={separator}
              onChange={(e) => handleSeparatorChange(e.target.value)}
              className="w-12 px-2 py-2 border border-gray-300 rounded-md text-center focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              maxLength={1}
            />
          </div>
        </div>
        
        {/* Action buttons */}
        <div className="flex space-x-2">
          <button
            onClick={clearAllElements}
            className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded-md hover:bg-gray-700 dark:bg-gray-500 dark:hover:bg-gray-600"
          >
            Clear All
          </button>
          <button
            onClick={handleSaveTemplate}
            disabled={!pattern.trim() || !validation.isValid}
            className="px-3 py-1.5 text-sm bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Save as Template
          </button>
          {saveStatus === 'saving' && (
            <span className="flex items-center text-sm text-gray-600 dark:text-gray-400">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-current mr-2"></div>
              Saving...
            </span>
          )}
          {saveStatus === 'saved' && (
            <span className="text-sm text-green-600 dark:text-green-400">✓ Saved</span>
          )}
          {saveStatus === 'error' && (
            <span className="text-sm text-red-600 dark:text-red-400">✗ Error</span>
          )}
        </div>
      </div>

      {/* Pattern Preview */}
      {(patternPreview.examples.length > 0 || patternPreview.isLoading || patternPreview.error) && (
        <div className="bg-lex-accent-soft border border-lex-accent rounded-md p-4 dark:bg-lex-accent-soft dark:border-lex-accent">
          <label className="block text-sm font-medium text-lex-accent dark:text-lex-accent mb-2">
            Pattern Preview
          </label>
          {patternPreview.isLoading && (
            <div className="flex items-center space-x-2 text-sm text-lex-accent dark:text-lex-accent">
              <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
              <span>Generating preview...</span>
            </div>
          )}
          {patternPreview.error && (
            <p className="text-sm text-red-600 dark:text-red-400">{patternPreview.error}</p>
          )}
          {patternPreview.examples.length > 0 && !patternPreview.isLoading && (
            <div>
              <p className="text-xs text-lex-accent dark:text-lex-accent mb-2">
                Example engagement numbers:
              </p>
              <div className="space-y-1">
                {patternPreview.examples.map((example, index) => (
                  <div key={index} className="font-mono text-sm text-lex-accent dark:text-lex-accent bg-white dark:bg-lex-accent-soft px-3 py-1 rounded border border-lex-accent dark:border-lex-accent">
                    {example}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Selected Elements */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Pattern Elements ({selectedElements.length})
        </label>
        <div className="flex flex-wrap gap-2 min-h-[50px] p-3 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-800 dark:border-gray-600">
          {selectedElements.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Click elements below to add them to your pattern
            </p>
          ) : (
            selectedElements.map((element, index) => (
              <div
                key={`${element.id}-${index}`}
                className="flex items-center space-x-2 bg-lex-accent-soft text-lex-accent px-3 py-1 rounded-full text-sm font-medium dark:bg-lex-accent-soft dark:text-lex-accent"
              >
                <span>{element.name}</span>
                <button
                  onClick={() => removeElement(index)}
                  className="text-lex-accent hover:text-lex-accent-hover dark:text-lex-accent dark:hover:text-lex-accent font-semibold"
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Available Elements */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Available Elements
        </label>
        
        {Object.entries(groupedElements).map(([category, elements]) => (
          <div key={category} className="mt-6">
            <div className="bg-gray-100 dark:bg-gray-700 px-4 py-3 rounded-lg border-l-4 border-lex-accent mb-3">
              <h4 className="text-base font-semibold text-gray-900 dark:text-white">
                {category}
                <span className="ml-2 text-sm font-normal text-gray-600 dark:text-gray-400">
                  ({elements.length} available)
                </span>
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {elements.map((element) => (
                <button
                  key={element.id}
                  onClick={() => addElement(element)}
                  className="flex items-center justify-between p-3 text-left border border-gray-200 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors"
                >
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {element.name}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {element.description}
                    </div>
                  </div>
                  <div className="text-xs font-mono bg-gray-100 text-gray-600 px-2 py-1 rounded dark:bg-gray-700 dark:text-gray-300">
                    {element.pattern}
                  </div>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Save Template Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Save Pattern as Template
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Enter template name..."
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={templateDescription}
                  onChange={(e) => setTemplateDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-lex-accent focus:border-lex-accent dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  rows={3}
                  placeholder="Describe when to use this template..."
                />
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-md">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Pattern Preview:</p>
                <code className="text-sm font-mono text-gray-900 dark:text-gray-100">{pattern}</code>
              </div>
            </div>
            
            <div className="flex space-x-3 mt-6">
              <button
                onClick={() => setShowSaveModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={saveTemplate}
                disabled={!templateName.trim() || saveStatus === 'saving'}
                className="flex-1 px-4 py-2 bg-lex-accent text-lex-accent-contrast rounded-md hover:bg-lex-accent-hover disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {saveStatus === 'saving' ? 'Saving...' : 'Save Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
